import { Link, Outlet, useLocation, useNavigate, useRouteLoaderData, useSearchParams } from "react-router-dom";
import { ReactComponent as Download } from "src/img/svg/download.svg";
import UserInfo from "src/components/common/userInfo";
import { useAuthStore, type User } from "src/components/main/authStore";
import {
    myAxios,
    simpleStatusColorCode,
} from "src/components/common/functions";
import Export from "./export";
import type { ReportRecord } from "./types";
import ServerFooter from "../common/ServerFooter";
import { reportExtraFields } from "./actions_loaders";

const title = "Reports";
type ReportListItem = ReportRecord & {
    hasFile?: string;
    projectName?: string;
    stats?: string;
};

export default function Reports() {
    const { reports, total }:{ reports: ReportRecord[], total: number } = useRouteLoaderData("Reports")!;
    const user = useAuthStore(state => state.user) as User;
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const defaultApproval = searchParams.get("approval") ?? "";
    const handleViewPDF = async (id: string) => {
        const formData = new FormData();
        formData.append("id", id);
        const res = await myAxios({ fileName: "reports/getReportPDF.php", formData });
        if (!res) return;
        window.open(res.data, "_blank", "noopener,noreferrer");
    };

    const updateParams = (mutate: (params: URLSearchParams) => void) => {
        const params = new URLSearchParams(searchParams);
        mutate(params);
        if (!params.get("page")) {
            params.set("page", "1");
        }
        if (!params.get("limit")) {
            params.set("limit", "10");
        }
        setSearchParams(params, { replace: false });
        return params;
    };
    const onFilter = [...searchParams.keys()].filter(param => !reportExtraFields.includes(param)).length > 0;
    const approvalReports = reports.some(report => report.approverId === user.id);
    const filteredUserList: ReportListItem[] = reports
        .filter(report => {
            if (defaultApproval === "all") {
                return report.approverId === user.id;
            }
            if (defaultApproval === "need") {
                return report.approverId === user.id && report.status === "Submitted";
            }
            return true;
        })
        .map(report => ({
            ...report,
            projectName: report.projectName ?? "",
            stats: [
                report.pothole ? `${report.pothole}Ps` : "",
                report.ep ? `${report.ep}EPs` : "",
                report.manhole ? `${report.manhole}MHs` : "",
            ].filter(Boolean).join(", "),
            hasFile: report.pdfId ? "Yes" : "No",
        }));
    return (
        <>
            <div className="cssContent pb8rem">
                <nav className="my-3 d-flex flex-wrap align-items-center gap-3">
                    <button
                        type="button"
                        className="form-control inline"
                        onClick={() => {
                            const params = updateParams(params => {
                                params.set("page", "1");
                            });
                            navigate({ pathname: `/${title}`, search: params.toString() }, { replace: true });
                        }}
                    >
                        Load
                    </button>
                    {approvalReports ? (
                        <select
                            className="form-control inline"
                            value={defaultApproval}
                            onChange={e => {
                                const value = e.currentTarget.value;
                                updateParams(params => {
                                    if (value) {
                                        params.set("approval", value);
                                    } else {
                                        params.delete("approval");
                                    }
                                    params.set("page", "1");
                                });
                            }}
                        >
                            <option value="">All Reports</option>
                            <option value="all">All My Approval</option>
                            <option value="need">Need My Approval</option>
                        </select>
                    ) : null}
                    <Outlet/>
                    <Link className="btn btn-outline-primary pointer position-relative" to={{ pathname: "Filter", search: location.search }} replace>
                        Filter
                        {onFilter ? (
                            <span children="on" className="badge text-bg-primary position-absolute top-0 start-100 translate-middle" style={{ minWidth: "2.2em" }} />
                        ) : null}
                    </Link>
                    <Export filterUserList={filteredUserList} />
                    <Link className="btn btn-outline-primary pointer" to={{ pathname: "Add", search: location.search }}>
                        Add
                    </Link>
                </nav>
                {!filteredUserList.length ? (
                    <div className="cssLoading">Empty</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table">
                            <thead className="noTableTopBorder">
                                <tr>
                                    <th>Project</th>
                                    <th>Start Date</th>
                                    <th>End Date</th>
                                    <th>Stats</th>
                                    <th>Code</th>
                                    <th>Priority</th>
                                    <th>Pending on Other Department</th>
                                    <th>Supplementary Report</th>
                                    <th>File</th>
                                    <th>Report Tech</th>
                                    <th>Status</th>
                                    <th/>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUserList.map(report => {
                                    const reportIdText = String(report.id);
                                    return (
                                        <tr
                                            key={reportIdText}
                                            id={reportIdText}
                                            style={{ color: simpleStatusColorCode[report.status as keyof typeof simpleStatusColorCode] }}
                                        >
                                            <td>
                                                <Link id={report.projectId} to={`/Projects/${report.projectId}`}>
                                                    {report.projectName}
                                                </Link>
                                            </td>
                                            <td>{report.startDate}</td>
                                            <td>{report.endDate}</td>
                                            <td>{report.stats}</td>
                                            <td>{report.code}</td>
                                            <td>{report.priority}</td>
                                            <td>{report.pending}</td>
                                            <td>{report.sup}</td>
                                            <td>
                                                {report.pdfId ? (
                                                    <button
                                                        id={`${report.id} file`}
                                                        type="button"
                                                        className="btn btn-primary"
                                                        onClick={() => handleViewPDF(reportIdText)}
                                                    >
                                                        <Download/>
                                                    </button>
                                                ) : null}
                                            </td>
                                            <td>
                                                <UserInfo name={report.reportTech ?? ""} id={report.reportTechId} />
                                            </td>
                                            <td>{report.status}</td>
                                            <td>
                                                <Link
                                                    children="Detail"
                                                    to={reportIdText}
                                                    className="btn btn-outline-primary"
                                                />
                                            </td>
                                        </tr>
                                    )})}
                            </tbody>
                        </table>
                    </div>
                )}
                {total?
                <ServerFooter
                    length = {total}
                />
                :null}
            </div>
        </>
    );
}
